import { Billing } from "@/types";
import cn from "@/utils/class-names";
import { useState, useEffect, useContext } from "react";
import { Badge, Button, Text } from "rizzui";
import {
    createBilling,
    fetchBillings,
    deleteBilling,
} from "@/server/api-services";
import toast from "react-hot-toast";
import { HeaderCell } from "@/components/legacy-table";
import WidgetCard from "@/components/cards/widget-card";
import ControlledTable from "@/components/controlled-table";
import { formatDate } from "@/utils/format-date";
import { AddBillingModal } from "./add-Modal";
import { Link } from "react-router-dom";
import routes from "@/routers/routes";
import GenericModal from "@/components/common/GenericModal";
import { AuthContext } from "@/auth/authContext";
import { permissions } from "../../roles-permissions/permissions";

interface TrackingHistoryTableProps {
    orderId: string;
}

export default function BillingTable({ orderId }: TrackingHistoryTableProps) {
    const { role } = useContext(AuthContext);

    const [Billings, setBillings] = useState([] as Billing[]);

    useEffect(() => {
        if (orderId !== undefined) {
            getBillings();
        }
    }, [orderId]);

    async function getBillings() {
        const response = await fetchBillings(orderId);
        if (response.statusCode !== 200) {
            toast.error("Failed to fetch Billings");
            return;
        }

        var data = response.data as Billing[];

        data.sort(
            (a, b) =>
                new Date(b.createdDate).getTime() -
                new Date(a.createdDate).getTime(),
        );

        setBillings(data);
    }

    async function addBilling(Billing: Billing) {
        const response = await createBilling(
            orderId,
            {
                amount: Billing.debit,
                billingType: Billing.billingType,
                remarks: Billing.remarks,
            },
            "debit",
        );
        if (response.statusCode !== 200) {
            toast.error("Failed to add Billing");
            return;
        }
        getBillings();
    }

    async function addPayment(Billing: Billing) {
        const response = await createBilling(
            orderId,
            {
                amount: Billing.debit,
                billingType: Billing.billingType,
                remarks: Billing.remarks,
            },
            "credit",
        );
        if (response.statusCode !== 200) {
            toast.error("Failed to add Billing");
            return;
        }
        getBillings();
    }

    async function removeBilling(BillingId: string) {
        const response = await deleteBilling(BillingId);
        if (response.statusCode !== 200) {
            toast.error("Failed to delete Billing");
            return;
        }
        getBillings();
    }

    const getColumns = () => [
        {
            title: <span className="ms-6 block whitespace-nowrap">Date</span>,
            dataIndex: "createdDate",
            key: "createdDate",
            width: 200,
            render: (createdDate: string) => (
                <span className="ms-6 block">
                    <Text className="mb-1 font-medium text-gray-700">
                        {formatDate(new Date(createdDate), "MMMM D, YYYY")}
                    </Text>
                    <Text className="text-[13px] text-gray-500">
                        {formatDate(new Date(createdDate), "h:mm A")}
                    </Text>
                </span>
            ),
        },
        {
            title: <HeaderCell title="Billing Type" />,
            dataIndex: "billingType",
            key: "billingType",
            width: 200,
            render: (billingType: string) => <Text>{billingType}</Text>,
        },
        {
            title: <HeaderCell title="Billingd By" />,
            dataIndex: "client",
            key: "client",
            width: 200,
            render: (createdUser: string) => <Text>{createdUser}</Text>,
        },
        {
            title: <HeaderCell title="Remarks" />,
            dataIndex: "remarks",
            key: "remarks",
            width: 200,
            render: (remarks: string) => <Text>{remarks}</Text>,
        },
        {
            title: <HeaderCell title="Type" />,
            dataIndex: "debit",
            key: "debit",
            width: 200,
            render: (debit: number) => (
                <Badge variant="flat" color="success" size="sm">
                    {debit == 0 ? "Payment" : "Invoice"}
                </Badge>
            ),
        },
        {
            title: <HeaderCell title="debit" />,
            dataIndex: "debit",
            key: "debit",
            width: 200,
            render: (debit: number) => <Text>${debit}</Text>,
        },
        {
            title: <HeaderCell title="credit" />,
            dataIndex: "credit",
            key: "credit",
            width: 200,
            render: (credit: number) => <Text>${credit}</Text>,
        },
        role &&
            (role.includes(permissions.transactions.addTransaction) ||
                role.includes(permissions.system.admin)) && {
                title: <HeaderCell title="Actions" />,
                dataIndex: "transactionId",
                key: "transactionId",
                width: 200,
                render: (id: string) => (
                    <GenericModal
                        onConfirm={() => removeBilling(id)}
                        title="Delete"
                        buttonType="flat"
                        color="danger"
                        description="Are you sure you want to delete billing"
                    />
                ),
            },
    ].filter(Boolean);

    var totalDebitAmount = Billings.reduce((acc, curr) => acc + curr.debit, 0);

    var totalAmountCredit = Billings.reduce(
        (acc, curr) => acc + curr.credit,
        0,
    );

    return (
        <div id="printBillings" className={cn("w-full")}>
            <WidgetCard
                title="Billings"
                className={cn("flex flex-col")}
                headerClassName="widget-card-header flex-col sm:flex-row"
                action={
                    <div className="flex gap-3 justify-between">
                        {role &&
                            (role.includes(
                                permissions.transactions.addTransaction,
                            ) ||
                                role.includes(permissions.system.admin)) && (
                                <>
                                    <AddBillingModal
                                        hide={false}
                                        handleSave={addBilling}
                                        color="danger"
                                        buttonType="outline"
                                        title="Add Invoice"
                                        description="Add a new Billing to the order"
                                    />

                                    <AddBillingModal
                                        hide={false}
                                        handleSave={addPayment}
                                        color="primary"
                                        buttonType="solid"
                                        title="Add Payment"
                                        description="Add a new Billing to the order"
                                    />
                                </>
                            )}
                        <Link to={routes.orders.billingDetails(orderId)}>
                            <Button
                                variant="flat"
                                color="secondary"
                                className="mt-4 sm:mt-0 sm:ms-4"
                            >
                                View All
                            </Button>
                        </Link>
                    </div>
                }
            >
                <div className={cn("table-wrapper flex-grow-mx-5 lg:-mx-7")}>
                    {Billings ? (
                        <>
                            <ControlledTable
                                data={Billings}
                                columns={getColumns()}
                                scroll={{ x: "100%", y: 240 }}
                                sticky={true}
                                variant="modern"
                                className="mt-4"
                            />
                            <div className="flex justify-end mt-4 pe-7">
                                <h4 className="text-sm font-semibold text-gray-700">
                                    Total Amount to be Paid:{" "}
                                    {totalAmountCredit < totalDebitAmount ? (
                                        <span className="text-md font-bold text-red-500">
                                            $
                                            {totalDebitAmount -
                                                totalAmountCredit}
                                        </span>
                                    ) : (
                                        <span className="text-md font-bold text-green-500">
                                            $
                                            {totalAmountCredit -
                                                totalDebitAmount}
                                        </span>
                                    )}
                                </h4>
                            </div>
                        </>
                    ) : (
                        <p className="text-center">No Billing data found</p>
                    )}
                </div>
            </WidgetCard>
        </div>
    );
}
